import type { PropsWithChildren } from '@shared/utils';
import { createContext, useCallback, useMemo, useState } from 'react';

import { RoleUserAdditionModal, RoleUserDeletionModal } from '../../containers';

import type {
  RoleUserManagementModalContextValue,
  RoleUserManagementModalAction,
  RoleUserManagementModalCallbackValue,
} from './types';

const RoleUserManagementModalContext = createContext<
  RoleUserManagementModalContextValue | undefined
>(undefined);

type RoleUserManagementModalProviderProps = PropsWithChildren & {
  appRoleId: string;
};

export default function RoleUserManagementModalProvider(
  props: Readonly<RoleUserManagementModalProviderProps>,
) {
  const { children, appRoleId } = props;
  const [action, setAction] = useState<RoleUserManagementModalAction | null>(null);
  const [actionValue, setActionValue] = useState<RoleUserManagementModalCallbackValue | undefined>(
    undefined,
  );

  const handleSetAction = useCallback(
    (
      action: RoleUserManagementModalAction | null,
      actionValue?: RoleUserManagementModalCallbackValue,
    ) => {
      setAction(action);
      setActionValue(actionValue);
    },
    [setAction],
  );

  const handleClose = useCallback(() => {
    setAction(null);
  }, []);

  const contextValue: RoleUserManagementModalContextValue = useMemo(() => {
    return {
      setAction: handleSetAction,
      close: handleClose,
    };
  }, [handleSetAction, handleClose]);

  return (
    <RoleUserManagementModalContext.Provider value={contextValue}>
      {children}
      <RoleUserDeletionModal
        open={action === 'role-user-deletion'}
        onClose={handleClose}
        userId={actionValue?.userId ?? ''}
        appRoleId={appRoleId}
      />
      <RoleUserAdditionModal
        open={action === 'role-user-addition'}
        onClose={handleClose}
        appRoleId={appRoleId}
      />
    </RoleUserManagementModalContext.Provider>
  );
}

export { RoleUserManagementModalContext };
